import api from '@/api/base';
import type { ApiResponse, DocumentModel } from '@/api/types';

// API URLs for document management
const URLS = {
  documents: '/document',
  documentById: (id: string) => `/document/${id}`,
  uploadDocument: '/document/upload',
  downloadDocument: (id: string) => `/document/${id}/download`,
  documentStatus: (id: string) => `/document/${id}/status`,
  documentIncomplete: '/document/incomplete',
  documentFeedback: '/document/feedback',
  commentReset: '/document/commentReset',
  stepOne: '/document/stepOne',
  facture: '/document/facture',
  complete: '/document/complete',
} as const;

// Request payloads are loosely typed (the backend accepts partial document
// bodies and various admin action envelopes); responses use the shared
// ApiResponse<DocumentModel> envelope.
type DocumentPayload = Record<string, unknown>;

// Document API methods
const documentsApi = {
  // Get all documents
  getDocuments: (token?: string) => {
    return api.get<ApiResponse<DocumentModel[]>>(URLS.documents, { token });
  },

  // Get document by ID
  getDocumentById: (id: string, token?: string) => {
    return api.get<ApiResponse<DocumentModel>>(URLS.documentById(id), {
      token,
    });
  },

  // Create new document
  createDocument: (documentData: DocumentPayload, token?: string) => {
    return api.post<ApiResponse<DocumentModel>>(URLS.documents, documentData, {
      token,
    });
  },

  // Update document
  updateDocument: (
    id: string,
    documentData: DocumentPayload,
    token?: string,
  ) => {
    return api.patch<ApiResponse<DocumentModel>>(
      URLS.documentById(id),
      documentData,
      { token },
    );
  },

  // Delete document
  deleteDocument: (id: string, token?: string) => {
    return api.delete<ApiResponse<null>>(URLS.documentById(id), { token });
  },

  // Upload document file
  uploadDocument: (formData: FormData, token?: string) => {
    return api.post<ApiResponse<DocumentModel>>(URLS.uploadDocument, formData, {
      token,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Download document
  downloadDocument: (id: string, token?: string) => {
    return api.get<Blob>(URLS.downloadDocument(id), {
      token,
      responseType: 'blob',
    });
  },

  // Update document status
  updateDocumentStatus: (id: string, status: unknown, token?: string) => {
    return api.patch<ApiResponse<DocumentModel>>(
      URLS.documentStatus(id),
      { status },
      { token },
    );
  },

  // Mark document as incomplete
  markAsIncomplete: (requestData: DocumentPayload, token?: string) => {
    return api.patch<ApiResponse<DocumentModel>>(
      URLS.documentIncomplete,
      requestData,
      { token },
    );
  },

  // Submit document feedback
  submitFeedback: (feedbackData: DocumentPayload, token?: string) => {
    return api.post<ApiResponse<DocumentModel>>(
      URLS.documentFeedback,
      feedbackData,
      { token },
    );
  },

  // Submit cancellation comment
  submitCancellationComment: (
    documentId: string,
    comment: string,
    token?: string,
  ) => {
    return api.post<ApiResponse<DocumentModel>>(
      URLS.commentReset,
      { documentId, comment },
      { token },
    );
  },

  // Accept document (step one)
  acceptDocument: (documentId: string, token?: string) => {
    return api.patch<ApiResponse<DocumentModel>>(
      URLS.stepOne,
      { documentId },
      { token },
    );
  },

  // Update document step (admin confirms payment)
  updateDocumentStep: (data: DocumentPayload, token?: string) => {
    return api.patch<ApiResponse<DocumentModel>>(URLS.stepOne, data, { token });
  },

  // Submit facture/invoice for document
  submitFacture: (factureData: DocumentPayload, token?: string) => {
    return api.post<ApiResponse<DocumentModel>>(URLS.facture, factureData, {
      token,
    });
  },

  // Complete document request
  completeRequest: (completionData: DocumentPayload, token?: string) => {
    return api.post<ApiResponse<DocumentModel>>(URLS.complete, completionData, {
      token,
    });
  },
};

export default documentsApi;
